import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import styled from "styled-components";
import { useQuery, useMutation, gql } from "@apollo/client";
import { Button as LatitudeButton } from "@flexport/latitude-typescript";
import AdminSupplierGrid from "./grids/AdminSupplierGrid";
import ConfirmDialog from "../../components/ConfirmDialog";
import { isSessionExpiredError } from "../../contexts/SessionExpiredContext";

const ADMIN_SUPPLIERS = gql`
  query AdminSuppliers {
    admin {
      suppliers {
        id
        name
        subfolder
        clientId
        client {
          clientId
          client_name
          folder
        }
        createdAt
        updatedAt
      }
    }
  }
`;

const ADMIN_CLIENTS = gql`
  query AdminClients {
    admin {
      clients {
        clientId
        client_name
        folder
      }
    }
  }
`;

const CREATE_SUPPLIER = gql`
  mutation CreateSupplier($name: String!, $subfolder: String!, $clientId: ID!) {
    admin {
      createSupplier(name: $name, subfolder: $subfolder, clientId: $clientId) {
        id
        name
        subfolder
        clientId
      }
    }
  }
`;

const UPDATE_SUPPLIER = gql`
  mutation UpdateSupplier($id: ID!, $name: String, $subfolder: String) {
    admin {
      updateSupplier(id: $id, name: $name, subfolder: $subfolder) {
        id
        name
        subfolder
      }
    }
  }
`;

const DELETE_SUPPLIER = gql`
  mutation DeleteSupplier($id: ID!) {
    admin {
      deleteSupplier(id: $id)
    }
  }
`;

const SupplierListContainer = styled.div`
  margin-top: 20px;
`;

const FormOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const FormContainer = styled.div`
  background-color: white;
  padding: 24px;
  border-radius: 4px;
  width: 400px;
  max-width: 90%;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
`;

const FormTitle = styled.h2`
  margin-top: 0;
  font-size: 20px;
  margin-bottom: 16px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
`;

const FormField = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-size: 14px;

  &:focus {
    border-color: #40a9ff;
    outline: none;
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-size: 14px;

  &:focus {
    border-color: #40a9ff;
    outline: none;
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
  gap: 12px;
`;

const ErrorMessage = styled.div`
  color: #ff4d4f;
  margin-top: 10px;
  font-size: 14px;
`;

const LoaderContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
`;

const AddButtonContainer = styled.div`
  margin-bottom: 16px;
`;

const NoDataMessage = styled.div`
  text-align: center;
  padding: 20px;
  color: #666;
  font-style: italic;
`;

export type SupplierListRefType = {
  refetchSuppliers: () => void;
};

interface SupplierFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: {
    name: string;
    subfolder: string;
    clientId: string;
  }) => void;
  initialData?: {
    id: string;
    name: string;
    subfolder: string;
    clientId: string;
  };
  title: string;
  submitLabel: string;
  clients: Array<{
    clientId: string;
    client_name: string;
    folder: string;
  }>;
}

const SupplierForm: React.FC<SupplierFormProps> = ({
  isOpen,
  onClose,
  onSubmit,
  initialData,
  title,
  submitLabel,
  clients,
}) => {
  const [formData, setFormData] = useState({
    name: initialData?.name || "",
    subfolder: initialData?.subfolder || "",
    clientId: initialData?.clientId || clients[0]?.clientId || "",
  });
  const [error, setError] = useState("");

  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name,
        subfolder: initialData.subfolder,
        clientId: initialData.clientId,
      });
    } else {
      setFormData({
        name: "",
        subfolder: "",
        clientId: clients[0]?.clientId || "",
      });
    }
  }, [initialData, clients, isOpen]);

  if (!isOpen) return null;

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    // Validate form
    if (!formData.name.trim()) {
      setError("Supplier name is required");
      return;
    }

    if (!formData.subfolder.trim()) {
      setError("Subfolder is required");
      return;
    }

    if (!formData.clientId) {
      setError("Client selection is required");
      return;
    }

    onSubmit(formData);
  };

  return (
    <FormOverlay data-testid="supplier-form-overlay">
      <FormContainer data-testid="supplier-form-container">
        <FormTitle data-testid="supplier-form-title">{title}</FormTitle>
        <Form onSubmit={handleSubmit} data-testid="supplier-form">
          <FormField>
            <Label htmlFor="name">Supplier Name</Label>
            <Input
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter supplier name"
              data-testid="supplier-name-input"
            />
          </FormField>

          <FormField>
            <Label htmlFor="subfolder">Subfolder</Label>
            <Input
              id="subfolder"
              name="subfolder"
              value={formData.subfolder}
              onChange={handleChange}
              placeholder="Enter subfolder name"
              data-testid="supplier-subfolder-input"
            />
          </FormField>

          <FormField>
            <Label htmlFor="clientId">Client</Label>
            <Select
              id="clientId"
              name="clientId"
              value={formData.clientId}
              onChange={handleChange}
              data-testid="supplier-client-select"
            >
              <option value="">Select a client</option>
              {clients.map((client) => (
                <option key={client.clientId} value={client.clientId}>
                  {client.client_name}
                </option>
              ))}
            </Select>
          </FormField>

          {error && (
            <ErrorMessage data-testid="supplier-form-error">
              {error}
            </ErrorMessage>
          )}

          <ButtonGroup>
            <LatitudeButton
              label="Cancel"
              intent="basic"
              kind="hollow"
              size="m"
              isLoading={false}
              width="responsive"
              type="button"
              disabled={false}
              onClick={onClose}
              data-testid="supplier-form-cancel"
            >
              Cancel
            </LatitudeButton>
            <LatitudeButton
              label={submitLabel}
              intent="basic"
              kind="solid"
              size="m"
              isLoading={false}
              width="responsive"
              type="submit"
              disabled={false}
              data-testid="supplier-form-submit"
            >
              {submitLabel}
            </LatitudeButton>
          </ButtonGroup>
        </Form>
      </FormContainer>
    </FormOverlay>
  );
};

const AdminSupplierList = forwardRef<SupplierListRefType>((_, ref) => {
  const [isCreateModalOpen, setCreateModalOpen] = useState(false);
  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState<any>(null);

  // State for custom delete confirmation dialog
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [supplierToDelete, setSupplierToDelete] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const { loading, error, data, refetch } = useQuery(ADMIN_SUPPLIERS);
  const { data: clientsData } = useQuery(ADMIN_CLIENTS);

  const [createSupplier] = useMutation(CREATE_SUPPLIER, {
    onCompleted: () => {
      setCreateModalOpen(false);
      refetch();
    },
  });

  const [updateSupplier] = useMutation(UPDATE_SUPPLIER, {
    onCompleted: () => {
      setEditModalOpen(false);
      refetch();
    },
  });

  const [deleteSupplier] = useMutation(DELETE_SUPPLIER, {
    onCompleted: () => {
      refetch();
    },
  });

  // Expose refetch function to parent
  useImperativeHandle(ref, () => ({
    refetchSuppliers: refetch,
  }));

  const suppliers = data?.admin?.suppliers || [];
  const clients = clientsData?.admin?.clients || [];

  const handleCreateSupplier = (data: {
    name: string;
    subfolder: string;
    clientId: string;
  }) => {
    createSupplier({
      variables: {
        name: data.name,
        subfolder: data.subfolder,
        clientId: data.clientId,
      },
    });
  };

  const handleUpdateSupplier = (data: {
    name: string;
    subfolder: string;
    clientId: string;
  }) => {
    updateSupplier({
      variables: {
        id: selectedSupplier.id,
        name: data.name,
        subfolder: data.subfolder,
      },
    });
  };

  // Handle edit button click
  const handleEdit = (supplier: any) => {
    setSelectedSupplier(supplier);
    setEditModalOpen(true);
  };

  // Handle delete button click - shows confirmation dialog
  const handleDelete = (id: string) => {
    const supplier = suppliers.find((s: any) => s.id === id);
    if (!supplier) return;

    setSupplierToDelete({
      id,
      name: supplier.name,
    });
    setShowDeleteDialog(true);
  };

  // Confirm delete after dialog confirmation
  const confirmDelete = async () => {
    if (!supplierToDelete) return;

    try {
      await deleteSupplier({
        variables: { id: supplierToDelete.id },
      });
      setShowDeleteDialog(false);
      setSupplierToDelete(null);
    } catch (error) {
      console.error("Error deleting supplier:", error);
      // Skip alert when session-expired modal will handle the error
      if (!isSessionExpiredError()) {
        alert("Failed to delete supplier. Please try again.");
      }
      setShowDeleteDialog(false);
      setSupplierToDelete(null);
    }
  };

  if (loading) {
    return <LoaderContainer>Loading suppliers...</LoaderContainer>;
  }

  if (error) {
    return <div>Error loading suppliers: {error.message}</div>;
  }

  return (
    <SupplierListContainer>
      <AddButtonContainer>
        <LatitudeButton
          label="Add New Supplier"
          intent="basic"
          kind="hollow"
          size="m"
          isLoading={false}
          width="responsive"
          type="button"
          disabled={false}
          onClick={() => setCreateModalOpen(true)}
          data-testid="create-supplier-button"
        >
          Add New Supplier
        </LatitudeButton>
      </AddButtonContainer>

      {suppliers.length === 0 ? (
        <NoDataMessage>
          No suppliers found. Add your first supplier.
        </NoDataMessage>
      ) : (
        <AdminSupplierGrid
          rows={suppliers}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      )}

      {/* Custom delete confirmation dialog */}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        title="File Uploader"
        message={`Are you sure you want to permanently delete supplier "${supplierToDelete?.name}"?\n\nThis action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={confirmDelete}
        onCancel={() => {
          setShowDeleteDialog(false);
          setSupplierToDelete(null);
        }}
      />

      <SupplierForm
        isOpen={isCreateModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSubmit={handleCreateSupplier}
        title="Add New Supplier"
        submitLabel="Create"
        clients={clients}
      />

      <SupplierForm
        isOpen={isEditModalOpen}
        onClose={() => setEditModalOpen(false)}
        onSubmit={handleUpdateSupplier}
        initialData={selectedSupplier}
        title="Edit Supplier"
        submitLabel="Update"
        clients={clients}
      />
    </SupplierListContainer>
  );
});

export default AdminSupplierList;
